import { useRequest } from '../providers/useRequest';
import AppError from '../../errors/AppError';
import IEmployee from '../../interfaces/IEmployee';
import { useSearchParams } from 'react-router-dom';
import ITypeDocument from '../../interfaces/ITypeDocument';
import { IPaginationReturn } from '../../interfaces/IPaginationReturn';
import IProfessional from '../../interfaces/IProfessional';
import IDiseaseClassification from '../../interfaces/IDiseaseClassification';
import IMedicalCertificate from '../../interfaces/IMedicalCertificate';
import IProfessionalType from '../../interfaces/IProfessionalType';
import ILocalSettings from '../../interfaces/ILocalSettings';
import { useLocalStorage } from '../providers/useLocalStorage';
import IFormatForRecordingNumberOfHours from '../../interfaces/IFormatForRecordingNumberOfHours';

export default interface IWizardFilters {
  employeeId?: string | null;
  initialDate?: string | null;
  finalDate?: string | null;
}

const useWizardRequest = () => {
  const [searchParams] = useSearchParams();
  const path = 'wizard';
  const cpfAndBirthdayRequest = useRequest<IEmployee>();
  const typeDocumentsRequest = useRequest<ITypeDocument>();
  const professionalsRequest = useRequest<IProfessional>();
  const diseaserequest = useRequest<IDiseaseClassification>();
  const medicalCertificatRequest = useRequest<IMedicalCertificate>();
  const professionalRequest = useRequest<IProfessionalType>();
  const formatForRecordingNumberOfHoursRequest = useRequest<IFormatForRecordingNumberOfHours>();
  const overlapRequest = useRequest<boolean>();

  const localStorage = useLocalStorage();

  const getTenantDomain = () => {
    const localSettings: ILocalSettings = localStorage.getItem('localSettings');
    const urlDomain = window.location?.href?.split('tenantDomain=')[1]?.split('&')[0];

    return searchParams.get('tenantDomain') || localSettings?.tenantDomain || urlDomain;
  };

  const listByCpfAndBirthday = async (employee: IEmployee): Promise<IEmployee> => {
    return await cpfAndBirthdayRequest
      .post({
        path: `${path}/cpf-birthday`,
        body: employee,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listMedicalCertificate = async (
    employeeId: string,
    query: string,
    filters: IWizardFilters,
  ): Promise<IPaginationReturn<IMedicalCertificate[]>> => {
    return await medicalCertificatRequest

      .postAndGetManyPaginated({
        path: `${path}/medical-certificate/employee/${employeeId}${query}`,
        body: { initialDate: filters.initialDate, finalDate: filters.finalDate },
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listEmployeeById = async (EmployeeId: number): Promise<IEmployee> => {
    return await cpfAndBirthdayRequest
      .getOne({
        path: `${path}/employee-cerficicate-medical/${EmployeeId}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listTypeDocuments = async (query: string): Promise<IPaginationReturn<ITypeDocument[]>> => {
    return await typeDocumentsRequest
      .getManyPaginated({
        path: `${path}/types-documents?${query}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };
  const listTypeDocumentsById = async (typeDocumentId: number): Promise<ITypeDocument> => {
    return await typeDocumentsRequest
      .getOne({
        path: `${path}/types-documents/${typeDocumentId}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listProfessionalByRegisterNumber = async (state: string, registerNumber: string): Promise<IProfessional> => {
    return await professionalsRequest
      .getOne({
        path: `${path}/register-number?state=${state}&registerNumber=${registerNumber
          .replace('.', '')
          .replace(',', '')
          .replace('/', '')
          .replace(' ', '')
          .replace(';', '')
          .replace('+', '')}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listDiseaseClassification = async (filter: string): Promise<IDiseaseClassification[]> => {
    const url = filter
      ? `${path}/disease-classification?perPage=300&currentPage=1&orderBy=code&orderDirection=ASC&filter=${filter}`
      : `${path}/disease-classification?perPage=300&currentPage=1&orderBy=code&orderDirection=ASC`;
    return await diseaserequest
      .getManyPaginated({
        path: url,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result?.data;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listDiseaseClassificationById = async (id: number): Promise<IDiseaseClassification> => {
    return await diseaserequest
      .getOne({
        path: `${path}/disease-classification/${id}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const findByProtocolNumberNotAuth = async (protocolNumber: string): Promise<IMedicalCertificate> => {
    return await medicalCertificatRequest
      .getOne({
        path: `${path}/query-document/${protocolNumber}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const updateContactEmployee = async (id: number, email: string, phoneNumber: string): Promise<IEmployee> => {
    return await cpfAndBirthdayRequest
      .put({
        path: `${path}/update-contact/${id}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
        body: { email, phoneNumber },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listDiseaseClassificationByMedicalCertificateId = async (id: string): Promise<IDiseaseClassification[]> => {
    return await diseaserequest
      .getMany({
        path: `${path}/medical-certificate/disease-classification/${id}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const createDiseaseClassification = async (resource: IDiseaseClassification): Promise<IDiseaseClassification> => {
    return await diseaserequest
      .post({
        path: `${path}/medical-certificate/disease-classification`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
        body: resource,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const updateDiseaseClassification = async (
    id: string,
    resource: IDiseaseClassification,
  ): Promise<IDiseaseClassification> => {
    return await diseaserequest
      .put({
        path: `${path}/medical-certificate/disease-classification/${id}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
        body: resource,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const createMedicalCertificate = async (resource: IMedicalCertificate): Promise<IMedicalCertificate> => {
    return await medicalCertificatRequest
      .post({
        path: `${path}/medical-certificate`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
        body: resource,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const updateMedicalCertificate = async (id: string, resource: IMedicalCertificate): Promise<IMedicalCertificate> => {
    return await medicalCertificatRequest
      .put({
        path: `${path}/medical-certificate/${id}`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
        body: resource,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listProfessionalTypes = async (): Promise<IPaginationReturn<IProfessionalType[]>> => {
    return await professionalRequest
      .getManyPaginated({
        path: `${path}/professional-types?perPage=20&currentPage=1&orderBy=id&orderDirection=asc`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const sendAttachment = async (id: string, attachment: FormData): Promise<void> => {
    await medicalCertificatRequest
      .patch({
        path: `${path}/medical-certificate/attachment/${id}`,
        sendAuthorization: false,
        headers: {
          tenantDomain: getTenantDomain(),
          'content-type': 'multipart/form-data',
        },
        body: attachment,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const removeAttachment = async (attachmentId: string): Promise<void> => {
    await medicalCertificatRequest
      .remove({
        path: `${path}/medical-certificate/attachment/${attachmentId}`,
        sendAuthorization: false,
        headers: {
          tenantDomain: getTenantDomain(),
        },
      })
      .then(() => {})
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const sendWhatsappMessageSuccess = async (medicalCertificateId: string): Promise<void> => {
    await medicalCertificatRequest
      .put({
        path: `${path}/medical-certificate/whatsapp-message/send`,
        sendAuthorization: false,
        body: { id: medicalCertificateId, templateType: 1 },
        headers: {
          tenantDomain: getTenantDomain(),
        },
      })
      .then(() => {})
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const sendEmailSuccessMessage = async (
    medicalCertificateId: string,
    templateType: number,
    protocolNumber?: string,
    notificationEmails?: string,
    message?: string,
  ): Promise<void> => {
    await medicalCertificatRequest
      .put({
        path: `${path}/medical-certificate/email-message/send`,
        sendAuthorization: false,
        body: { id: medicalCertificateId, templateType, protocolNumber, notificationEmails, message },
        headers: {
          tenantDomain: getTenantDomain(),
        },
      })
      .then(() => {})
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getFormatForRecordingNumberOfHours = async (): Promise<IFormatForRecordingNumberOfHours> => {
    return await formatForRecordingNumberOfHoursRequest
      .getOne({
        path: `${path}/format-for-recording-number-of-hours`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const isOverlap = async (employeeId: number, documentDate: string): Promise<boolean> => {
    return await overlapRequest
      .post({
        path: `${path}/medical-certificate/is-overlaped`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
        body: { employeeId, documentDate },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };
  const FinishDocument = async (
    phoneNumber: number,
    email: string,
    templateType: number,
    id_MedicalCertificate: string,
  ): Promise<void> => {
    await overlapRequest
      .post({
        path: `${path}/finish`,
        sendAuthorization: false,
        headers: { tenantDomain: getTenantDomain() },
        body: { phoneNumber, email, templateType, medicalCertificateId: id_MedicalCertificate },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  return {
    listByCpfAndBirthday,
    listMedicalCertificate,
    listTypeDocuments,
    listProfessionalByRegisterNumber,
    listDiseaseClassification,
    listDiseaseClassificationById,
    listTypeDocumentsById,
    findByProtocolNumberNotAuth,
    updateContactEmployee,
    listEmployeeById,
    createMedicalCertificate,
    updateMedicalCertificate,
    listProfessionalTypes,
    sendAttachment,
    removeAttachment,
    sendWhatsappMessageSuccess,
    sendEmailSuccessMessage,
    listDiseaseClassificationByMedicalCertificateId,
    createDiseaseClassification,
    updateDiseaseClassification,
    getFormatForRecordingNumberOfHours,
    isOverlap,
    FinishDocument,
  };
};

export { useWizardRequest };
